import React from 'react';

import Text from 'relements/build/esm/input/text';
import Radio from 'relements/build/esm/input/radio';
import Toggle from 'relements/build/esm/input/toggle';
import Dropdown from 'relements/build/esm/input/dropdown';

import styled from 'styled-components';

import { sendGoogleAnalyticsEvents } from '@src/actions/_utils';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';
import { GOOGLE_ANALYTICS, FIELD_TO_GA_LABEL_MAPPING } from '@src/const';
import { FEATURE_FLAG_DATA } from '@src/constants/MogamboConstants';
import { getVoiceModels } from '@src/containers/AIAgents/store/api';
import { BOT_ENV } from './constants';

const STATUS_ACTIVE = { title: 'Active', value: true };
const STATUS_INACTIVE = { title: 'Inactive', value: false };

const TextArea = styled(Text)`
  textarea {
    max-height: 54px;
    overflow-y: auto;
    border: 1px solid
      ${(props) =>
        props.error
          ? props.theme.colors.red.haptik
          : props.theme.colors.grey.haptik};
  }
`;

export default class Advanced extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      errorKeys: [],
      active: STATUS_ACTIVE,
      gender: 'Male',
      companyName: '',
      botPersona: 0,
      botContext: '',
      companyContext: '',
      contextEnhancementInstr: '',
      intentDetectionInstr: '',
      answeringInstr: '',
      aiAgentAuthEnabled: false,
      aiAgentSessionTimeout: null,
      aiAgentSessionTimeoutEnabled: false,
      addDateAndTimeInPrompt: true,
      detectLanguageForAllMessages: false,
      aiAgentVoiceEnabled: false,
      aIAgentVoiceModel: '',
      aiAgentTtsEnabled: false,
      aiAgentPhoneNumber: '',
      aiAgentGreetingMessage: '',
      aiAgentVoiceForOutput: '',
      aiAgentVoiceLocale: '',
      aiAgentVoiceProvider: '',
      aiAgentVoiceModelOptions: [],
      aiAgentVoiceOutputOptions: [],
      aiAgentVoiceLocaleOptions: [],
      aiAgentsModelsData: [],
    };
  }

  componentDidMount() {
    this.fetchData();
  }

  onUpdate = (key, value) => {
    if (key === 'aIAgentVoiceModel') {
      const { aiAgentsModelsData } = this.state;
      const aiAgentVoiceProvider = aiAgentsModelsData?.find(
        (model) => model.id === value.id
      )?.meta_data.provider;
      const aiAgentVoiceOutputOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelVoices = model.meta_data?.voices?.map((voice) => {
            return { id: voice, name: voice };
          });
          return [...result, ...selectedModelVoices];
        },
        []
      );
      const aiAgentVoiceLocaleOptions = aiAgentsModelsData?.reduce(
        (result, model) => {
          if (model.id !== value.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];
          return [...result, ...selectedModelLocales];
        },
        []
      );
      this.setState({
        [key]: value,
        aiAgentVoiceProvider,
        aiAgentVoiceOutputOptions,
        aiAgentVoiceLocaleOptions,
        aiAgentVoiceForOutput: '',
        aiAgentVoiceLocale: '',
      });
    } else this.setState({ [key]: value });

    if (FIELD_TO_GA_LABEL_MAPPING[key]) {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.BOT,
        GOOGLE_ANALYTICS.EVENTS.BOT_EDIT,
        FIELD_TO_GA_LABEL_MAPPING[key],
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
    }
  };

  validate = () => {
    const errorKeys = [];
    const {
      companyContext,
      botContext,
      contextEnhancementInstr,
      intentDetectionInstr,
      answeringInstr,
      aiAgentSessionTimeout,
      aiAgentSessionTimeoutEnabled,
      aiAgentPhoneNumber,
    } = this.state;
    const timeoutValueRegex = /^\d+$/;
    const phoneNumberValueRegex = /^\+?\d{7,15}$/;

    if (companyContext && companyContext.split(' ').length > 300)
      errorKeys.push('companyContext');
    if (
      contextEnhancementInstr &&
      contextEnhancementInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('contextEnhancementInstr');
    if (
      intentDetectionInstr &&
      intentDetectionInstr.trim().split(/\s+/).length > 300
    )
      errorKeys.push('intentDetectionInstr');
    if (answeringInstr && answeringInstr.trim().split(/\s+/).length > 300)
      errorKeys.push('answeringInstr');
    if (botContext && botContext.split(' ').length > 300)
      errorKeys.push('botContext');
    if (this.state.active === undefined) errorKeys.push('active');
    if (!this.state.gender) errorKeys.push('gender');
    if (this.state.botPersona === undefined) errorKeys.push('botPersona');
    if (aiAgentSessionTimeout && !timeoutValueRegex.test(aiAgentSessionTimeout))
      errorKeys.push('aiAgentSessionTimeout');
    if (aiAgentPhoneNumber && !phoneNumberValueRegex.test(aiAgentPhoneNumber))
      errorKeys.push('aiAgentPhoneNumber');
    if (
      aiAgentSessionTimeoutEnabled &&
      (!aiAgentSessionTimeout || Number(aiAgentSessionTimeout) < 1)
    )
      errorKeys.push('aiAgentSessionTimeout');

    this.setState({ errorKeys });

    return errorKeys.length === 0;
  };

  async fetchData() {
    const domain =
      (CommonStore.getMetaTags().domain?.length &&
        CommonStore.getMetaTags().domain) ||
      JSON.parse(localStorage.getItem('ww_iva'));
    const domainId = domain?.id;
    if (!domainId) return;
    try {
      const { response } = await getVoiceModels();
      if (response.success) {
        const data = response.body;
        const aiAgentVoiceModelOptions = data.reduce((result, model) => {
          const newModel = { id: model.id, name: model.name };
          return [...result, newModel];
        }, []);
        const aiAgentVoiceOutputOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;
          const selectedModelVoices =
            model.meta_data?.voices?.map((voice) => {
              return { id: voice, name: voice };
            }) || [];
          return [...result, ...selectedModelVoices];
        }, []);
        const aiAgentVoiceLocaleOptions = data.reduce((result, model) => {
          if (model.id !== this.state.aIAgentVoiceModel.id) return result;
          const selectedModelLocales = model.meta_data.locales
            ? Object.entries(model.meta_data.locales).map(([key, value]) => {
                return { id: value, name: key };
              })
            : [];

          return [...result, ...selectedModelLocales];
        }, []);
        let aiAgentVoiceLocale = this.state.aiAgentVoiceLocale;

        aiAgentVoiceLocaleOptions.forEach((locale) => {
          if (locale.id === aiAgentVoiceLocale) {
            aiAgentVoiceLocale = { id: aiAgentVoiceLocale, name: locale.name };
          }
        });
        this.setState({
          aiAgentsModelsData: data,
          aiAgentVoiceModelOptions,
          aiAgentVoiceOutputOptions,
          aiAgentVoiceLocaleOptions,
          aiAgentVoiceLocale,
        });
      }
    } catch (error) {
      console.error('Error fetching AI agent data:', error);
    }
  }

  render() {
    const { active_env } = this.props.botEnv;
    const ENV_OBJ =
      active_env && BOT_ENV.hasOwnProperty(active_env.toUpperCase())
        ? BOT_ENV[active_env.toUpperCase()]
        : BOT_ENV.TEST;
    const isAissIntentEnable = BotStore.getFeatureFlag(
      FEATURE_FLAG_DATA.FLAGS.ENABLE_AISS_INTENT
    );
    const currentDomainInStore = CommonStore.getMetaTags().domain;

    return (
      <div>
        <div className="grid-container">
          <div className="grid-label">
            * Bot deployment environment (base URL)
          </div>
          <div className="radio-wrapper">
            <Radio
              themed
              prefixClassName="env-options"
              value={ENV_OBJ}
              options={Object.values(BOT_ENV)}
              onChange={(value) =>
                this.props.handleEnvUpdate(`active_env`, value.value)
              }
            />
          </div>
          <div className="input-wrapper">
            <Text
              themed
              style={{ marginBottom: 8 }}
              className="url-input"
              placeholder={BOT_ENV.TEST.placeholder}
              value={this.props.botEnv[`base_url_${BOT_ENV.TEST.value}`]}
              onChange={(value) =>
                this.props.handleEnvUpdate(
                  `base_url_${BOT_ENV.TEST.value}`,
                  value
                )
              }
            />
            <Text
              themed
              className="url-input"
              placeholder={BOT_ENV.PRODUCTION.placeholder}
              value={this.props.botEnv[`base_url_${BOT_ENV.PRODUCTION.value}`]}
              onChange={(value) =>
                this.props.handleEnvUpdate(
                  `base_url_${BOT_ENV.PRODUCTION.value}`,
                  value
                )
              }
            />
          </div>
        </div>
        <Radio
          themed
          options={this.props.personalities}
          label="* Personality"
          value={this.props.personalities[this.state.botPersona]}
          onChange={(value) => this.onUpdate('botPersona', value.key)}
          error={this.state.errorKeys.indexOf('botPersona') > -1}
          className="bot-properties-input"
        />
        <Radio
          themed
          options={[STATUS_ACTIVE, STATUS_INACTIVE]}
          label="* Bot Status"
          className="bot-properties-input"
          value={this.state.active}
          onChange={(value) => this.onUpdate('active', value)}
          error={this.state.errorKeys.indexOf('active') > -1}
        />
        <Text
          themed
          label="* Gender"
          placeholder="Write here..."
          className="bot-properties-input"
          value={this.state.gender}
          onChange={(value) => this.onUpdate('gender', value)}
          error={this.state.errorKeys.indexOf('gender') > -1}
        />
        <Text
          themed
          label="Name of the Creator / Company"
          placeholder="(Optional) Write here..."
          className="bot-properties-input"
          value={this.state.companyName}
          onChange={(value) => this.onUpdate('companyName', value)}
        />
        <Text
          themed
          label="Name of the app"
          placeholder="(Optional) Write here..."
          className="bot-properties-input"
          value={this.state.appName}
          onChange={(value) => this.onUpdate('appName', value)}
        />
        <Text
          themed
          label="Feedback Smart Action Link"
          placeholder="(Optional) Write here..."
          className="bot-properties-input"
          value={this.state.feedbackSmartActionLink}
          onChange={(value) => this.onUpdate('feedbackSmartActionLink', value)}
        />
        <Text
          themed
          label="Company Website"
          placeholder="(Optional) Write here..."
          className="bot-properties-input"
          value={this.state.companyWebsite}
          onChange={(value) => this.onUpdate('companyWebsite', value)}
        />
        <Text
          themed
          label="Where does the bot live?"
          placeholder="(Optional) Write here..."
          className="bot-properties-input"
          value={this.state.botLocation}
          onChange={(value) => this.onUpdate('botLocation', value)}
        />
        <TextArea
          multiline
          themed
          label="Bot Description (Max 300 Words)"
          placeholder="(Optional) Describe the purpose of the bot in a few words here..."
          className="bot-properties-input"
          value={this.state.botContext}
          onChange={(value) => {
            this.onUpdate('botContext', value);
          }}
          error={this.state.errorKeys.indexOf('botContext') > -1}
        />
        <TextArea
          multiline
          themed
          label="Company Description (Max 300 Words)"
          placeholder="(Optional) Write a brief note about the Company, and the services it provides, here..."
          className="bot-properties-input"
          value={this.state.companyContext}
          onChange={(value) => this.onUpdate('companyContext', value.trim())}
          error={this.state.errorKeys.indexOf('companyContext') > -1}
        />
        <TextArea
          multiline
          themed
          label="Context Enhancement Instructions (Max 300 Words)"
          placeholder="(Optional) Write a brief instruction you want to provide to GPT during context enhancement."
          className="bot-properties-input"
          value={this.state.contextEnhancementInstr}
          onChange={(value) =>
            this.onUpdate('contextEnhancementInstr', value.trim())
          }
          error={this.state.errorKeys.indexOf('contextEnhancementInstr') > -1}
          border
        />
        {isAissIntentEnable && (
          <TextArea
            multiline
            themed
            label="Intent Detection Instructions (Max 300 Words)"
            placeholder="(Optional) Write a brief instruction you want to provide to GPT during intent detection."
            className="bot-properties-input"
            value={this.state.intentDetectionInstr}
            onChange={(value) =>
              this.onUpdate('intentDetectionInstr', value.trim())
            }
            error={this.state.errorKeys.indexOf('intentDetectionInstr') > -1}
          />
        )}
        <TextArea
          multiline
          themed
          label="AISS Answering/Inference Instructions (Max 300 Words)"
          placeholder="(Optional) Write a brief instruction you want to provide to GPT during answering/inference."
          className="bot-properties-input"
          value={this.state.answeringInstr}
          onChange={(value) => this.onUpdate('answeringInstr', value.trim())}
          error={this.state.errorKeys.indexOf('answeringInstr') > -1}
        />
        {currentDomainInStore.domain_type === 2 && (
          <>
            <Toggle
              themed
              value={this.state.aiAgentAuthEnabled}
              onChange={(value) => this.onUpdate('aiAgentAuthEnabled', value)}
              label="Authentication"
            />
            <Toggle
              themed
              disabled={!this.state.aiAgentAuthEnabled}
              value={this.state.aiAgentSessionTimeoutEnabled}
              onChange={(value) =>
                this.onUpdate('aiAgentSessionTimeoutEnabled', value)
              }
              label="Session Timeout"
            />
            <Text
              themed
              disabled={
                !this.state.aiAgentAuthEnabled ||
                !this.state.aiAgentSessionTimeoutEnabled
              }
              label="Duration of the Session in minutes"
              placeholder="Session Duration in Minutes"
              className="bot-properties-input"
              value={this.state.aiAgentSessionTimeout}
              onChange={(value) => {
                if (!value?.trim())
                  this.onUpdate('aiAgentSessionTimeout', null);
                else this.onUpdate('aiAgentSessionTimeout', value?.trim());
              }}
              error={this.state.errorKeys.indexOf('aiAgentSessionTimeout') > -1}
            />
            <Toggle
              themed
              value={this.state.addDateAndTimeInPrompt}
              onChange={(value) =>
                this.onUpdate('addDateAndTimeInPrompt', value)
              }
              label="Keep AI Agent aware of Date and Time"
            />
          </>
        )}
        <Toggle
          themed
          value={this.state.detectLanguageForAllMessages}
          onChange={(value) =>
            this.onUpdate('detectLanguageForAllMessages', value)
          }
          label="Detect language for all messages"
        />
        {currentDomainInStore.domain_type === 2 && (
          <>
            <Toggle
              themed
              value={this.state.aiAgentVoiceEnabled}
              onChange={(value) => this.onUpdate('aiAgentVoiceEnabled', value)}
              label="Enable Voice"
            />
            <Dropdown
              themed
              withSearch
              options={this.state.aiAgentVoiceModelOptions || []}
              className="bot-properties-input"
              label="Select Model"
              disabled={
                BotStore.isProductionEnv() || !this.state.aiAgentVoiceEnabled
              }
              hint="You can add more languages after the bot has been created."
              placeholder="Select items"
              optionKey="name"
              searchKeys={['name']}
              value={
                this.state.aIAgentVoiceModel.name
                  ? this.state.aIAgentVoiceModel
                  : this.state.aiAgentVoiceModelOptions.find(
                      (model) => model.id === this.state.aIAgentVoiceModel.id
                    )
              }
              onChange={(value) => this.onUpdate('aIAgentVoiceModel', value)}
            />
            {/* <Toggle
              themed
              value={this.state.aiAgentTtsEnabled}
              onChange={(value) => this.onUpdate('aiAgentTtsEnabled', value)}
              label="Enable TTS"
              disabled={!this.state.aiAgentVoiceEnabled}
            /> */}
            <Text
              themed
              disabled={
                BotStore.isProductionEnv() || !this.state.aiAgentVoiceEnabled
              }
              label="Phone Number"
              placeholder="Phone Number"
              className="bot-properties-input"
              value={this.state.aiAgentPhoneNumber}
              onChange={(value) => {
                if (!value?.trim()) this.onUpdate('aiAgentPhoneNumber', '');
                else this.onUpdate('aiAgentPhoneNumber', value?.trim());
              }}
              error={
                this.state.errorKeys.indexOf('aiAgentPhoneNumber') > -1 &&
                'Please enter a valid phone number.'
              }
              errorText
            />
            <Text
              themed
              disabled={
                BotStore.isProductionEnv() || !this.state.aiAgentVoiceEnabled
              }
              label="Greeting Message"
              placeholder="Greeting Message"
              className="bot-properties-input"
              value={this.state.aiAgentGreetingMessage}
              onChange={(value) => {
                if (!value?.trim()) this.onUpdate('aiAgentGreetingMessage', '');
                else this.onUpdate('aiAgentGreetingMessage', value?.trim());
              }}
              error={
                this.state.errorKeys.indexOf('aiAgentGreetingMessage') > -1
              }
            />
            <Dropdown
              themed
              withSearch
              options={this.state.aiAgentVoiceOutputOptions || []}
              className="bot-properties-input"
              label="Voice for Output"
              disabled={
                BotStore.isProductionEnv() || !this.state.aiAgentVoiceEnabled
              }
              hint="You can add more languages after the bot has been created."
              placeholder="Select items"
              optionKey="name"
              searchKeys={['name']}
              value={this.state.aiAgentVoiceForOutput || {}}
              onChange={(value) =>
                this.onUpdate('aiAgentVoiceForOutput', value)
              }
            />
            <Dropdown
              themed
              withSearch
              options={this.state.aiAgentVoiceLocaleOptions}
              className="bot-properties-input"
              label="Locale"
              disabled={
                BotStore.isProductionEnv() ||
                !this.state.aiAgentVoiceEnabled ||
                !this.state.aiAgentVoiceLocaleOptions?.length
              }
              placeholder="Select locale"
              optionKey="name"
              searchKeys={['name']}
              value={this.state.aiAgentVoiceLocale || {}}
              onChange={(value) => this.onUpdate('aiAgentVoiceLocale', value)}
            />
          </>
        )}
      </div>
    );
  }
}
